import type { ColorName } from '@emotion/react';
import type {
  ChargeItemDefinitionMoney,
  ClaimResponseItemTotal,
  ClaimResponseModelGetResponse,
  ClaimResponseOutcome,
  PartialClaim,
} from '@quromedical/models';
import { compact } from '@quromedical/utils';
import { Col, Row } from 'components/base';
import {
  Divider,
  FormCard,
  inputDateFormatter,
  type RowConfig,
  Skeleton,
  Spacer,
  Text,
} from 'design-system';
import { MARGIN } from 'design-system/theme';
import { getMoneyDisplay } from 'helpers/money';
import { strings } from 'strings';

interface ClaimResponseCardProps {
  claimResponse: ClaimResponseModelGetResponse;
  claim: PartialClaim;
}

interface TableData extends Partial<ClaimResponseItemTotal> {
  name?: string;
  notes?: string[];
}

const totalDisplay = (
  amount?: ChargeItemDefinitionMoney,
): string | undefined => (amount ? getMoneyDisplay(amount, 1) : undefined);

const formRows = (
  data: ClaimResponseModelGetResponse,
  claim: PartialClaim,
): RowConfig[] => {
  const { balanceOutstanding, claimedAmount, paidProvider } = data.total || {};
  const tableData =
    data.items?.map<TableData>((d) => ({
      ...(d?.total || {}),
      name: claim.chargeLines?.find((_, index) => d?.sequence === index + 1)
        ?.chargeLine.name,
      notes: compact(d?.notes),
    })) || [];

  const itemFields = tableData.map<RowConfig>((line) => ({
    fields: [
      {
        type: 'custom',
        Display: () => <Text variant="body-strong">{line.name}</Text>,
      },
      {
        type: 'custom',
        Display: () => (
          <Text variant="body-strong">{totalDisplay(line.billedAmount)}</Text>
        ),
      },
      {
        type: 'custom',
        Display: () => (
          <Text variant="body-strong">
            {totalDisplay(line.medicalAidPayable)}
          </Text>
        ),
      },
      {
        type: 'custom',
        Display: () => (
          <Text variant="body-strong">{totalDisplay(line.medicalAidRate)}</Text>
        ),
      },
      {
        type: 'custom',
        Display: () => (
          <Col>
            {line.notes
              ? line.notes.map((note) => (
                  <Row key={note}>
                    <Text>{note}</Text>
                  </Row>
                ))
              : null}
          </Col>
        ),
      },
    ],
  }));

  const spacer = {
    type: 'custom',
    Display: () => null,
  } as const;

  return [
    {
      fields: [
        {
          type: 'text',
          label: strings.LabelStatus,
          display: data.status,
        },
        {
          type: 'text',
          label: strings.ClaimResponseOutcome,
          display: data.outcome,
        },
        spacer,
        spacer,
        spacer,
      ],
    },
    {
      fields: [
        {
          type: 'text',
          label: strings.ClaimResponseStatusDescription,
          display: data.statusDescription,
        },
        {
          type: 'text',
          label: strings.ClaimResponseClaimedAmount,
          display: totalDisplay(claimedAmount),
        },
        {
          type: 'text',
          label: strings.ClaimResponsePaidProvider,
          display: totalDisplay(paidProvider),
        },
        {
          type: 'text',
          label: strings.ClaimResponseBalanceOutstanding,
          display: totalDisplay(balanceOutstanding),
        },
        {
          type: 'list',
          label: strings.ClaimResponseNotes,
          display: compact(data.notes),
        },
      ],
    },
    {
      fields: [
        {
          type: 'custom',
          Display: () => (
            <Col flex={1}>
              <Spacer height={MARGIN.xs} />
              <Row flex={1}>
                <Divider flex={1} orientation="horizontal" color="divider" />
              </Row>
              <Spacer height={MARGIN.xs} />
            </Col>
          ),
        },
      ],
    },
    {
      fields: [
        {
          type: 'text',
          label: strings.ClaimResponseItem,
        },
        {
          type: 'text',
          label: strings.ClaimResponseBilledAmount,
        },
        {
          type: 'text',
          label: strings.ClaimResponseMedicalAidPayable,
        },
        {
          type: 'text',
          label: strings.ClaimResponseMedicalAidRate,
        },
        {
          type: 'text',
          label: strings.ClaimResponseNotes,
        },
      ],
    },
    ...itemFields,
  ];
};

const borderStatusColorMap: Record<
  Zod.infer<typeof ClaimResponseOutcome>,
  ColorName
> = {
  complete: 'status-success',
  error: 'status-critical',
  partial: 'status-warning',
  queued: 'divider',
};

export const ClaimResponseCard: React.FC<ClaimResponseCardProps> = ({
  claimResponse,
  claim,
}) => {
  const rows = formRows(claimResponse, claim);

  const { date, outcome } = claimResponse;
  const color = outcome ? borderStatusColorMap[outcome] : undefined;
  const title = date ? inputDateFormatter.datetime(new Date(date)) : '';

  return (
    <Skeleton>
      <FormCard title={title} borderColor={color} rows={rows} />
    </Skeleton>
  );
};
